import { renderRichText, unescapeHtmlEntities } from '@/utils/stringUtils';
import type { StructuredCard } from './types';
import { useCoachChatStore } from '@/app/stores/coachChatStore';
import { cn } from '@/utils/uiUtils';

export type GeneralContent = {
  text?: string;
  intro?: string;
  message?: string;
  chatResponse?: string;
  cards?: StructuredCard[];
  suggested_actions?: string[];
};

export const General = ({
  content,
  renderCard,
  onAction,
  messageId,
}: {
  content: GeneralContent;
  renderCard: (card: StructuredCard, idx: number) => React.ReactNode;
  onAction?: (action: string) => void;
  messageId?: string;
}) => {
  // Use persistent store to track if this specific message's actions have already been triggered.
  // This prevents double-clicks and preserves the disabled state across chat window re-opens.
  const { clickedMessageIds, markMessageClicked } = useCoachChatStore();
  const hasClicked = messageId ? clickedMessageIds.includes(messageId) : false;

  const handleAction = (ans: string) => {
    if (hasClicked || !messageId) return;
    markMessageClicked(messageId);
    onAction?.(ans);
  };

  const rawText =
    content?.text || content?.message || content?.chatResponse || '';
  const textNodes = renderRichText(rawText);
  return (
    <div className='flex flex-col gap-3'>
      {content.intro && (
        <div className='mb-1 text-slate-900 dark:text-slate-100 italic bg-emerald-50 dark:bg-emerald-950/30 p-3 rounded-lg border border-emerald-100 dark:border-emerald-900/50'>
          {renderRichText(content.intro)}
        </div>
      )}
      <div className='prose whitespace-pre-line'>{textNodes}</div>
      {content.suggested_actions && content.suggested_actions.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-2'>
          {content.suggested_actions.map((ans: string) => (
            <button
              key={ans}
              type='button'
              disabled={hasClicked}
              onClick={() => handleAction(ans)}
              className={cn(
                'px-3 py-1.5 font-medium rounded-full text-xs transition-all shadow-sm border-none',
                hasClicked
                  ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                  : 'bg-yellow-400 hover:bg-yellow-500 text-slate-900 cursor-pointer active:scale-95'
              )}
            >
              {unescapeHtmlEntities(ans)}
            </button>
          ))}
        </div>
      )}
      {content.cards && content.cards.length > 0 && (
        <div className='flex flex-row flex-wrap gap-2 mt-1'>
          {content.cards.map((card, idx) => renderCard(card, idx))}
        </div>
      )}
    </div>
  );
};
